// src/components/vendor/VendorSupportPage.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { vendorApi } from '../../api/vendorApi';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  CardActionArea,
  Stack,
  Badge,
} from '@mui/material';
import {
  Add,
  Email,
  Chat,
  Help,
  ConfirmationNumber,
  CheckCircle,
  Schedule,
  Close,
  Refresh,
  Reply,
  Visibility,
  Send,
  Phone,
  AccessTime,
  Check,
  Warning,
  Storefront,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const VendorSupportPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [faqs, setFaqs] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [openTicketDetail, setOpenTicketDetail] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    message: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ============================================================
  // FETCH DATA
  // ============================================================

  const fetchTickets = async () => {
    try {
      const response = await vendorApi.getSupportTickets();
      setTickets(response.tickets || []);
      setStats(response.stats || {});
    } catch (error) {
      console.error('Error fetching tickets:', error);
      showSnackbar('Failed to fetch tickets', 'error');
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await vendorApi.getVendorFaqs();
      setFaqs(response.faqs || {});
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchFaqs()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // ============================================================
  // HANDLERS
  // ============================================================

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      showSnackbar('Subject and message are required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await vendorApi.createSupportTicket(newTicket);
      showSnackbar('Ticket created successfully');
      setOpenTicketDialog(false);
      setNewTicket({ subject: '', category: 'general', priority: 'medium', message: '' });
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      showSnackbar('Failed to create ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyTicket = async () => {
    if (!replyMessage.trim()) {
      showSnackbar('Reply message is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await vendorApi.replySupportTicket(selectedTicket.id, { message: replyMessage });
      showSnackbar('Reply sent successfully');
      setReplyMessage('');
      fetchTickets();
      
      const response = await vendorApi.getSupportTicket(selectedTicket.id);
      setSelectedTicket(response.ticket);
    } catch (error) {
      console.error('Error sending reply:', error);
      showSnackbar('Failed to send reply', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;

    setSubmitting(true);
    try {
      await vendorApi.closeSupportTicket(ticketId);
      showSnackbar('Ticket closed successfully');
      fetchTickets();
      if (selectedTicket) {
        setSelectedTicket({ ...selectedTicket, status: 'closed' });
      }
    } catch (error) {
      console.error('Error closing ticket:', error);
      showSnackbar('Failed to close ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================

  const getStatusColor = (status) => {
    const colors = {
      open: '#4CAF50',
      'in-progress': '#FF9800',
      resolved: '#2196F3',
      closed: '#9E9E9E'
    };
    return colors[status] || '#9E9E9E';
  };

  const getStatusBgColor = (status) => {
    const colors = {
      open: 'success',
      'in-progress': 'warning',
      resolved: 'info',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#4CAF50',
      medium: '#FF9800',
      high: '#F44336',
      urgent: '#D32F2F'
    };
    return colors[priority] || '#9E9E9E';
  };

  const getPriorityBgColor = (priority) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'open': return <Email sx={{ color: '#4CAF50' }} />;
      case 'in-progress': return <Schedule sx={{ color: '#FF9800' }} />;
      case 'resolved': return <CheckCircle sx={{ color: '#2196F3' }} />;
      case 'closed': return <Close sx={{ color: '#9E9E9E' }} />;
      default: return <ConfirmationNumber />;
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ============================================================
  // RENDER COMPONENTS
  // ============================================================

  const renderStatCard = (label, value, color, icon) => (
    <Grid item xs={6} sm={3}>
      <Card sx={{ 
        height: '100%',
        borderLeft: `4px solid ${color}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
      }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {icon && <Box sx={{ color }}>{icon}</Box>}
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ color }}>
              {value || 0}
            </Typography>
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
            }}
          >
            {label}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderMobileTicketCard = (ticket) => (
    <Card key={ticket.id} sx={{ 
      mb: 2, 
      borderRadius: 2, 
      overflow: 'hidden',
      bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
    }}>
      <CardActionArea onClick={() => {
        setSelectedTicket(ticket);
        setOpenTicketDetail(true);
      }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1} minWidth={0}>
                <Typography 
                  variant="subtitle1" 
                  fontWeight="bold" 
                  noWrap
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                  }}
                >
                  {ticket.subject || 'No subject'}
                </Typography>
                <Typography 
                  variant="caption"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                  }}
                >
                  {ticket.ticketNumber || `#${ticket.id}`}
                </Typography>
              </Box>
              <Box display="flex" gap={0.5} flexShrink={0}>
                <Chip
                  label={getStatusLabel(ticket.status)}
                  size="small"
                  color={getStatusBgColor(ticket.status)}
                  sx={{ height: 20, fontSize: '0.6rem' }}
                />
                <Chip
                  label={ticket.priority || 'N/A'}
                  size="small"
                  color={getPriorityBgColor(ticket.priority)}
                  sx={{ height: 20, fontSize: '0.6rem' }}
                />
              </Box>
            </Box>

            <Typography 
              variant="caption"
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
              }}
            >
              {ticket.category || 'General'} • {formatDate(ticket.createdAt)}
            </Typography>

            {ticket.SupportReplies && ticket.SupportReplies.length > 0 && (
              <Typography 
                variant="caption"
                sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                }}
              >
                {ticket.SupportReplies.length} {ticket.SupportReplies.length === 1 ? 'reply' : 'replies'}
              </Typography>
            )}

            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTicket(ticket);
                  setOpenTicketDetail(true);
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                View
              </Button>
              {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Close />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseTicket(ticket.id);
                  }}
                  sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                >
                  Close
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, pb: { xs: 10, sm: 12 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold"
            sx={{ 
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
            }}
          >
            Support Center
          </Typography>
          <Typography 
            variant="body2"
            sx={{ 
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
            }}
          >
            Get help and support for your vendor account
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenTicketDialog(true)}
          size={isMobile ? 'small' : 'medium'}
          fullWidth={isMobile}
        >
          New Ticket
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={isMobile ? 1 : 3} sx={{ mb: 4 }}>
        {renderStatCard('Total', stats.total, theme.palette.primary.main, <ConfirmationNumber fontSize="small" />)}
        {renderStatCard('Open', stats.open, '#4CAF50', <Email fontSize="small" />)}
        {renderStatCard('In Progress', stats.inProgress, '#FF9800', <Schedule fontSize="small" />)}
        {renderStatCard('Resolved', stats.resolved, '#2196F3', <CheckCircle fontSize="small" />)}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ 
        mb: 3, 
        borderRadius: 2, 
        overflow: 'hidden',
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
      }}>
        <Tabs
          value={selectedTab}
          onChange={(e, v) => setSelectedTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? 'fullWidth' : 'standard'}
          centered={!isMobile}
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 56 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
              '&.Mui-selected': {
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
              }
            }
          }}
        >
          <Tab icon={<ConfirmationNumber />} label={isMobile ? 'Tickets' : 'My Tickets'} />
          <Tab icon={<Help />} label={isMobile ? 'FAQs' : 'FAQs'} />
          <Tab icon={<Chat />} label={isMobile ? 'Chat' : 'Live Chat'} />
        </Tabs>
      </Paper>

      {/* ============================================================ */}
      {/* TAB 0: TICKETS */}
      {/* ============================================================ */}
      {selectedTab === 0 && (
        <Box>
          {tickets.length === 0 ? (
            <Paper sx={{ 
              p: { xs: 4, sm: 6 }, 
              textAlign: 'center', 
              borderRadius: 2,
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
            }}>
              <ConfirmationNumber sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                }}
              >
                No Tickets Found
              </Typography>
              <Typography 
                variant="body2" 
                paragraph
                sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                }}
              >
                You haven't created any support tickets yet.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenTicketDialog(true)}
              >
                Create New Ticket
              </Button>
            </Paper>
          ) : (
            <>
              {isMobile ? (
                // Mobile Card View
                <Box>
                  {tickets.map((ticket) => renderMobileTicketCard(ticket))}
                </Box>
              ) : (
                // Desktop List View
                <List>
                  {tickets.map((ticket) => (
                    <Paper key={ticket.id} sx={{ 
                      mb: 2, 
                      overflow: 'hidden', 
                      borderRadius: 2,
                      bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
                    }}>
                      <ListItem sx={{ py: 2 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: getStatusColor(ticket.status) + '20' }}>
                            {getStatusIcon(ticket.status)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography 
                                variant="subtitle1" 
                                fontWeight="bold"
                                sx={{ 
                                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                                }}
                              >
                                {ticket.subject}
                              </Typography>
                              <Chip
                                label={getStatusLabel(ticket.status)}
                                size="small"
                                color={getStatusBgColor(ticket.status)}
                              />
                              <Chip
                                label={ticket.priority}
                                size="small"
                                color={getPriorityBgColor(ticket.priority)}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography 
                                variant="body2"
                                sx={{ 
                                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                                }}
                              >
                                Ticket #{ticket.ticketNumber} • {ticket.category}
                              </Typography>
                              <Typography 
                                variant="body2"
                                sx={{ 
                                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                                }}
                              >
                                Created: {formatDate(ticket.createdAt)}
                              </Typography>
                              {ticket.SupportReplies && ticket.SupportReplies.length > 0 && (
                                <Typography 
                                  variant="caption"
                                  sx={{ 
                                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'text.secondary'
                                  }}
                                >
                                  {ticket.SupportReplies.length} {ticket.SupportReplies.length === 1 ? 'reply' : 'replies'}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Box display="flex" gap={1} flexShrink={0}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setOpenTicketDetail(true);
                            }}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                          >
                            View
                          </Button>
                          {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<Close />}
                              onClick={() => handleCloseTicket(ticket.id)}
                              sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                              Close
                            </Button>
                          )}
                        </Box>
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>
      )}

      {/* ============================================================ */}
      {/* TAB 1: FAQS */}
      {/* ============================================================ */}
      {selectedTab === 1 && (
        <Box>
          {Object.keys(faqs).length === 0 ? (
            <Paper sx={{ 
              p: { xs: 4, sm: 6 }, 
              textAlign: 'center', 
              borderRadius: 2,
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
            }}>
              <Help sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                }}
              >
                No FAQs Available
              </Typography>
              <Typography 
                variant="body2"
                sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                }}
              >
                Check back later for frequently asked questions.
              </Typography>
            </Paper>
          ) : (
            Object.entries(faqs).map(([category, categoryFaqs]) => (
              <Box key={category} sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    textTransform: 'capitalize', 
                    mb: 2, 
                    fontWeight: 600,
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                  }}
                >
                  {category}
                </Typography>
                {categoryFaqs.map((faq) => (
                  <Card key={faq.id} sx={{ 
                    mb: 1, 
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
                  }}>
                    <CardContent>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        sx={{ 
                          color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                        }}
                      >
                        {faq.question}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 0.5,
                          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                        }}
                      >
                        {faq.answer}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ))
          )}
        </Box>
      )}

      {/* ============================================================ */}
      {/* TAB 2: LIVE CHAT */}
      {/* ============================================================ */}
      {selectedTab === 2 && (
        <Card sx={{ 
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
        }}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <Chat sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
              }}
            >
              Live Chat Support
            </Typography>
            <Typography 
              variant="body2" 
              paragraph
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
              }}
            >
              Chat with our support team for immediate assistance.
            </Typography>
            <Box display="flex" justifyContent="center" gap={{ xs: 2, sm: 4 }} flexWrap="wrap" sx={{ mb: 3 }}>
              <Box textAlign="center">
                <AccessTime sx={{ color: 'primary.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                  Avg. Wait Time
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                  &lt; 1 min
                </Typography>
              </Box>
              <Box textAlign="center">
                <Check sx={{ color: 'success.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                  Availability
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                  24/7
                </Typography>
              </Box>
              <Box textAlign="center">
                <Phone sx={{ color: 'info.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                  Support
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                  Live Agents
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<Chat />}
              onClick={() => window.open('/vendor/chat', '_blank')}
              fullWidth={isMobile}
            >
              Start Live Chat
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ============================================================ */}
      {/* DIALOG: CREATE TICKET */}
      {/* ============================================================ */}
      <Dialog open={openTicketDialog} onClose={() => setOpenTicketDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
            Create Support Ticket
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
            Describe your issue and we'll get back to you
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subject"
            value={newTicket.subject}
            onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
            sx={{ mt: 2 }}
            required
            InputLabelProps={{
              sx: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit' }
            }}
            InputProps={{
              sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
            }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit' }}>
              Category
            </InputLabel>
            <Select
              value={newTicket.category}
              label="Category"
              onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
              sx={{
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                '& .MuiSelect-icon': {
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                }
              }}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="account">Account</MenuItem>
              <MenuItem value="products">Products</MenuItem>
              <MenuItem value="orders">Orders</MenuItem>
              <MenuItem value="payments">Payments</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit' }}>
              Priority
            </InputLabel>
            <Select
              value={newTicket.priority}
              label="Priority"
              onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
              sx={{
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                '& .MuiSelect-icon': {
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                }
              }}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            value={newTicket.message}
            onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
            sx={{ mt: 2 }}
            required
            InputLabelProps={{
              sx: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit' }
            }}
            InputProps={{
              sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenTicketDialog(false)} fullWidth={isMobile}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTicket} disabled={submitting} fullWidth={isMobile}>
            {submitting ? <CircularProgress size={24} /> : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* DIALOG: TICKET DETAIL */}
      {/* ============================================================ */}
      <Dialog open={openTicketDetail} onClose={() => setOpenTicketDetail(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1}>
            <Box>
              <Typography variant="h6" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                Ticket Details
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                {selectedTicket?.ticketNumber || `#${selectedTicket?.id}`}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenTicketDetail(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <>
              <Box mb={3}>
                <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                  Subject
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                  {selectedTicket.subject}
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                    Status
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedTicket.status)}
                    color={getStatusBgColor(selectedTicket.status)}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                    Priority
                  </Typography>
                  <Chip
                    label={selectedTicket.priority}
                    color={getPriorityBgColor(selectedTicket.priority)}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                    Category
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, textTransform: 'capitalize', color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                    {selectedTicket.category || 'General'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                    Created
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                    {formatDate(selectedTicket.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                    Message
                  </Typography>
                  <Paper variant="outlined" sx={{ 
                    p: 2, 
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50', 
                    mt: 0.5 
                  }}>
                    <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                      {selectedTicket.message}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                Replies ({selectedTicket.SupportReplies?.length || 0})
              </Typography>
              {selectedTicket.SupportReplies && selectedTicket.SupportReplies.length > 0 ? (
                <List>
                  {selectedTicket.SupportReplies.map((reply) => (
                    <Paper key={reply.id} sx={{ 
                      mb: 1, 
                      p: 2, 
                      bgcolor: reply.isAdmin ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50') : 'background.paper' 
                    }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: reply.isAdmin ? 'primary.main' : 'secondary.main' }}>
                            {reply.isAdmin ? 'A' : 'V'}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                            {reply.isAdmin ? 'Support Team' : 'You'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                          {formatDate(reply.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                        {reply.message}
                      </Typography>
                    </Paper>
                  ))}
                </List>
              ) : (
                <Typography sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }} align="center" py={2}>
                  No replies yet
                </Typography>
              )}

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
                    Add Reply
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    sx={{ mb: 1 }}
                    InputLabelProps={{
                      sx: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'inherit' }
                    }}
                    InputProps={{
                      sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                    }}
                  />
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      endIcon={<Send />}
                      onClick={handleReplyTicket}
                      disabled={!replyMessage.trim() || submitting}
                    >
                      {submitting ? <CircularProgress size={24} /> : 'Send Reply'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={() => handleCloseTicket(selectedTicket.id)}
                      disabled={submitting}
                    >
                      Close Ticket
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTicketDetail(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* SNACKBAR */}
      {/* ============================================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VendorSupportPage;